'use client';

import CabinPhotoNote from '@/components/property/CabinPhotoNote';
import { resortName } from '@/data/property-locations';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';
import type { Property } from '@/data/properties';
import SmartImage from '@/components/ui/SmartImage';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';

type PropertyGridProps = {
  properties: Property[];
  columns?: 2 | 3;
};

export default function PropertyGrid({
  properties,
  columns = 3,
}: PropertyGridProps) {
  const t = useTranslations('PropertyCard');

  function specsLine(p: Property): string {
    const parts: string[] = [];
    if (p.guests) parts.push(t('sleeps', { count: p.guests }));
    if (p.bedrooms) parts.push(t('bed', { count: p.bedrooms }));
    if (p.hasSauna) parts.push(t('sauna'));
    if (p.hasHotTub) parts.push(t('hotTub'));
    return parts.join(' · ') || p.tagline;
  }
  const live = properties.filter((p) => p.status === 'live');
  const comingSoon = properties.filter((p) => p.status === 'coming-soon');

  return (
    <div className="space-y-6">
      {/* Live cabins */}
      <div
        className={cn(
          'grid gap-6',
          'grid-cols-1 md:grid-cols-2',
          columns === 3 && 'lg:grid-cols-3'
        )}
      >
        {live.map((property, index) => (
          <motion.div
            key={property.slug}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Link href={`/cabins/${property.slug}`} className="block group">
              <Card>
                <div className="relative aspect-[3/2] overflow-hidden">
                  {property.images?.[0] ? (
                    <><SmartImage
                      src={property.images[0]}
                      alt={property.name}
                      width={600}
                      height={400}
                      fill
                      className={cn(
                        "object-cover transition-transform duration-500 group-hover:scale-105",
                        property.slug === "varakka-yllas" && "object-[center_30%]"
                      )}
                      category="cabin"
                    /><CabinPhotoNote slug={property.slug} src={property.images[0]} /></>
                  ) : (
                    <div className="w-full h-full bg-[#F0EDE5] flex items-center justify-center">
                      <span className="text-[#8A8A9A] text-sm">{t('photoComingSoon')}</span>
                    </div>
                  )}
                </div>

                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant={property.resort}>
                      {resortName(property.resort)}
                    </Badge>
                  </div>

                  <h2 className="font-sans text-xl font-light mb-1">
                    {property.name}
                  </h2>

                  <p className="text-sm text-[#5A5A6E] mb-3">
                    {specsLine(property)}
                  </p>

                  <span className="text-sm font-medium text-[#9B7B3A] group-hover:underline">
                    {t('viewCabin')} →
                  </span>
                </div>
              </Card>
            </Link>
          </motion.div>
        ))}
      </div>

      {/* Coming soon cabins */}
      {comingSoon.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
          {comingSoon.map((property, index) => (
            <motion.div
              key={property.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (live.length + index) * 0.1 }}
            >
              <Card className="opacity-60">
                <div className="relative aspect-[3/2]">
                  <div className="w-full h-full bg-[#F0EDE5] flex items-center justify-center">
                    <span className="font-sans text-lg font-semibold text-[#8A8A9A]">
                      {t('comingSoon')}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant={property.resort}>
                      {resortName(property.resort)}
                    </Badge>
                    <Badge variant="coming-soon">{t('comingSoon')}</Badge>
                  </div>
                  <h2 className="font-sans text-xl font-light">
                    {property.name}
                  </h2>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
